/**
 * Panel lateral UDV AI Copilot — preinforme + triage.
 */
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import CircularProgress from '@material-ui/core/CircularProgress'
import {
  mdiClose,
  mdiAutoFix,
  mdiContentCopy,
  mdiFileDocumentOutline,
} from '@mdi/js'
import { t } from '../i18n'
import { COPILOT_MODEL_OPTIONS, COPILOT_MODELS } from '../services/geminiCopilot'
import '../skins/copilot.css'

function triageClass(level) {
  const L = String(level || '').toUpperCase()
  if (L === 'RED') return 'is-red'
  if (L === 'YELLOW') return 'is-yellow'
  if (L === 'GREEN') return 'is-green'
  return ''
}

function triageLabel(level) {
  const L = String(level || '').toUpperCase()
  if (L === 'RED') return t('copilot.triageRed')
  if (L === 'YELLOW') return t('copilot.triageYellow')
  if (L === 'GREEN') return t('copilot.triageGreen')
  return '—'
}

/**
 * @param {object} props
 */
function CopilotPanel({
  open,
  skin = 'classic',
  layout = 'dock-right',
  loading = false,
  model = COPILOT_MODELS.FLASH,
  result = null,
  hallazgos = '',
  impresion = '',
  recomendacion = '',
  onClose,
  onModelChange,
  onAnalyze,
  onChangeHallazgos,
  onChangeImpresion,
  onChangeRecomendacion,
  onInsert,
  disabledAnalyze = false,
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const root = document.documentElement
    if (open && layout === 'dock-right') {
      root.classList.add('udv-copilot-dock-right-open')
    } else {
      root.classList.remove('udv-copilot-dock-right-open')
    }
    if (open && layout === 'dock-bottom') {
      root.classList.add('udv-copilot-dock-bottom-open')
    } else {
      root.classList.remove('udv-copilot-dock-bottom-open')
    }
    return () => {
      root.classList.remove('udv-copilot-dock-right-open')
      root.classList.remove('udv-copilot-dock-bottom-open')
    }
  }, [open, layout])

  if (!open) return null

  const panel = (
    <div
      className={`udv-copilot-panel layout-${layout} skin-${skin}`}
      role="complementary"
      aria-label={t('copilot.title')}
    >
      <header className="udv-copilot-header">
        <div className="udv-copilot-title">
          <Icon path={mdiAutoFix} size={0.85} color="currentColor" />
          <span>{t('copilot.title')}</span>
        </div>
        <button
          type="button"
          className="udv-copilot-icon-btn"
          onClick={onClose}
          aria-label={t('common.close')}
          title={t('common.close')}
        >
          <Icon path={mdiClose} size={0.9} color="currentColor" />
        </button>
      </header>

      <div className="udv-copilot-body">
        <p className="udv-copilot-disclaimer">{t('copilot.disclaimer')}</p>

        <label className="udv-copilot-field-label" htmlFor="udv-copilot-model">
          {t('copilot.model')}
        </label>
        <select
          id="udv-copilot-model"
          className="udv-copilot-select"
          value={model}
          disabled={!!loading}
          onChange={(e) => onModelChange && onModelChange(e.target.value)}
        >
          {COPILOT_MODEL_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id} disabled={!opt.enabled}>
              {opt.enabled ? opt.label : `${opt.label} (${t('copilot.comingSoon')})`}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="udv-copilot-analyze"
          disabled={!!loading || !!disabledAnalyze}
          onClick={onAnalyze}
        >
          {loading ? (
            <>
              <CircularProgress size={16} thickness={5} style={{ color: 'inherit' }} />
              <span>{t('copilot.analyzing')}</span>
            </>
          ) : (
            <>
              <Icon path={mdiAutoFix} size={0.85} color="currentColor" />
              <span>{t('copilot.analyze')}</span>
            </>
          )}
        </button>

        {result ? (
          <div className="udv-copilot-results">
            <div
              className={`udv-copilot-triage ${triageClass(result.triage_level)}`}
              role="status"
            >
              <span className="udv-copilot-triage-dot" aria-hidden />
              <div className="udv-copilot-triage-text">
                <strong>{triageLabel(result.triage_level)}</strong>
                {result.urgency_reason ? (
                  <span className="udv-copilot-triage-reason">
                    {result.urgency_reason}
                  </span>
                ) : null}
              </div>
            </div>

            <label className="udv-copilot-field-label" htmlFor="udv-copilot-hallazgos">
              {t('copilot.hallazgos')}
            </label>
            <textarea
              id="udv-copilot-hallazgos"
              className="udv-copilot-textarea"
              rows={6}
              value={hallazgos}
              onChange={(e) => onChangeHallazgos && onChangeHallazgos(e.target.value)}
            />

            <label className="udv-copilot-field-label" htmlFor="udv-copilot-impresion">
              {t('copilot.impresion')}
            </label>
            <textarea
              id="udv-copilot-impresion"
              className="udv-copilot-textarea"
              rows={3}
              value={impresion}
              onChange={(e) => onChangeImpresion && onChangeImpresion(e.target.value)}
            />

            <label
              className="udv-copilot-field-label"
              htmlFor="udv-copilot-recomendacion"
            >
              {t('copilot.recomendacion')}
            </label>
            <textarea
              id="udv-copilot-recomendacion"
              className="udv-copilot-textarea"
              rows={2}
              value={recomendacion}
              onChange={(e) =>
                onChangeRecomendacion && onChangeRecomendacion(e.target.value)
              }
            />

            <button
              type="button"
              className="udv-copilot-insert"
              onClick={onInsert}
              disabled={!!loading}
            >
              <Icon path={mdiFileDocumentOutline} size={0.85} color="currentColor" />
              <span>{t('copilot.insert')}</span>
              <Icon path={mdiContentCopy} size={0.7} color="currentColor" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )

  if (layout === 'rail') {
    return panel
  }

  return createPortal(
    <div className={`udv-copilot-sheet layout-${layout} skin-${skin}`}>{panel}</div>,
    document.body
  )
}

export default CopilotPanel
